import { useEffect, useRef, useState } from 'react';
import * as styles from "./app.module.scss";
import cn from 'classnames';
import { ButtonResult, ButtonType, popup } from 'ui';

type QualityClass = '0' | '1' | '2' | '3' | '4' | '5';
type QualityItem = {
    code: string;
    url: string;
    image: string;
}

const translation = (window as any).app.preloadState.translation;
const selectedCode: string = (window as any).app.preloadState.qualitySelectorCode;
const data: Partial<Record<QualityClass, QualityItem[]>> = (window as any).app.preloadState.qualitySelector;
const texts: {
    classTitle: string;
    classDescription: string;
    variantTitle: string;
    variantDescription: string;
} = (window as any).app.preloadState.qualitySelectorTexts;

function isSelected(item: QualityItem | null | undefined): boolean {
    return item?.code === selectedCode;
}

const classesInOrder = (['5', '4', '3', '2', '1'] as const);

const worstQualityItemUrl = classesInOrder.map(c => data[c]?.[0]).find(d => d != null)?.url;
const isNewSelected = data[0]?.[0]?.code === selectedCode;
const startingSelectedClass = Object.entries(data).find(p => p[1].some(item => item.code === selectedCode))?.[0] as QualityClass | undefined;

function App() {
    const [selectedClass, setSelectedClass] = useState<QualityClass | undefined>(startingSelectedClass);
    const [buttonState, setButtonState] = useState<[boolean, boolean]>([false, false]);
    const variantContainer = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = variantContainer.current;
        if (container) {
            container.scrollLeft = 0;
            setButtonState([false, 0 < container.scrollWidth - container.clientWidth]);
            function scrollF(this: HTMLDivElement) {
                setButtonState([this.scrollLeft > 0, this.scrollLeft < this.scrollWidth - this.clientWidth]);
            }
            container.addEventListener('scroll', scrollF);
            return () => variantContainer.current?.removeEventListener('scroll', scrollF);
        }
    }, [selectedClass]);

    if (!worstQualityItemUrl) return;

    const goLeft = () => {
        const container = variantContainer.current;
        if (!container) return;
        container.scrollLeft -= container.offsetWidth / 2;
    }

    const goRight = () => {
        const container = variantContainer.current;
        if (!container) return;
        container.scrollLeft += container.offsetWidth / 2;
    }

    const openClassPopup = async () => {
        await popup(texts.classTitle, texts.classDescription, [{
            label: translation['common.okButtonLabel'],
            result: ButtonResult.Cancel,
            type: ButtonType.Primary
        }]);
    }

    const openVariantPopup = async () => {
        await popup(texts.variantTitle, texts.variantDescription, [{
            label: translation['common.okButtonLabel'],
            result: ButtonResult.Cancel,
            type: ButtonType.Primary
        }]);
    }

    return <>
        <div className={styles.qualityCondition}>
            <p>{translation['qualitySelector.condition']}</p>
            <div>
                <a href={data[0]?.[0]?.url} className={cn(!(data[0]?.[0]) && styles.disabled)} aria-selected={isNewSelected}>{translation['qualitySelector.new']}</a>
                <a href={worstQualityItemUrl} className={cn(!worstQualityItemUrl && styles.disabled)} aria-selected={!isNewSelected}>{translation['qualitySelector.used']}</a>
            </div>
        </div>
        {!isNewSelected && <>
            <div className={styles.qualityClass}>
                <p>{translation['qualitySelector.selectClass']}
                    {(texts.classTitle.length > 0 || texts.classDescription.length > 0) && <span className={styles.infoIcon} onClick={openClassPopup}></span>}
                </p>
                <div>
                    {classesInOrder.map(i => <button key={i} aria-selected={selectedClass == i} disabled={!data[i] || !data[i].length} onClick={() => setSelectedClass(i)}>{i}</button>)}
                </div>
            </div>
            {selectedClass !== undefined && <>
                <div className={styles.qualityVariant}>
                    <p>{translation['qualitySelector.selectVariant']}
                        {(texts.variantTitle.length > 0 || texts.variantDescription.length > 0) && <span className={styles.infoIcon} onClick={openVariantPopup}></span>}
                    </p>
                    <div>
                        <div ref={variantContainer}>
                            {data[selectedClass]?.map(item => <a key={item.url} aria-selected={isSelected(item)} href={item.url}><img src={item.image} /></a>)}
                        </div>
                        {buttonState[0] && <button className={styles.left} onClick={goLeft}></button>}
                        {buttonState[1] && <button className={styles.right} onClick={goRight}></button>}
                    </div>
                </div>
            </>}
        </>}
    </>
}

export default App;
